import React, { useEffect, useState, useCallback } from 'react';
import { Mail, RefreshCw, Trash2, UserPlus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { tabService, type WorkspaceInvitation, type WorkspaceRole } from '@/services/tabs';
import { RoleBadge } from './RoleBadge';
import { toast } from 'sonner';

interface PendingInvitesCardProps {
  workspaceId: string;
  isAdmin: boolean;
  refreshKey: number;
  onInviteClick: () => void;
  onChange: () => void;
}

function relativeExpiry(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const days = Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'expired';
  if (days === 0) return 'expires today';
  if (days === 1) return 'expires in 1 day';
  return `expires in ${days} days`;
}

export const PendingInvitesCard: React.FC<PendingInvitesCardProps> = ({
  workspaceId,
  isAdmin,
  refreshKey,
  onInviteClick,
  onChange,
}) => {
  const [invites, setInvites] = useState<WorkspaceInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<WorkspaceInvitation | null>(null);

  const fetchInvites = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await tabService.getWorkspaceInvitations(workspaceId);
      setInvites(rows);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, isAdmin]);

  useEffect(() => {
    void fetchInvites();
  }, [fetchInvites, refreshKey]);

  const handleResend = async (inv: WorkspaceInvitation) => {
    try {
      await tabService.inviteToWorkspace(workspaceId, inv.email, inv.role as WorkspaceRole);
      toast.success(`Re-sent invite to ${inv.email}`);
      void fetchInvites();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to resend invitation');
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await tabService.cancelWorkspaceInvitation(workspaceId, cancelTarget.id);
      toast.success('Invitation cancelled');
      setCancelTarget(null);
      void fetchInvites();
      onChange();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to cancel invitation');
    }
  };

  if (!isAdmin) return null;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-khiimori-text-primary flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Pending invitations
          {invites.length > 0 && (
            <span className="text-xs font-normal text-khiimori-text-muted">({invites.length})</span>
          )}
        </h3>
        <Button size="sm" variant="secondary" onClick={onInviteClick} className="gap-1.5">
          <UserPlus className="w-4 h-4" />
          Invite
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-khiimori-text-muted">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : invites.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted">No pending invitations.</p>
      ) : (
        <ul className="space-y-2">
          {invites.map((inv) => (
            <li
              key={inv.id}
              className="flex items-center gap-3 p-2 rounded-md bg-khiimori-bg-elevated/40 border border-khiimori-border/40"
            >
              <Mail className="w-4 h-4 text-khiimori-text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-khiimori-text-primary truncate">{inv.email}</p>
                <p className="text-xs text-khiimori-text-muted">{relativeExpiry(inv.expires_at)}</p>
              </div>
              <RoleBadge role={inv.role} iconless />
              <button
                type="button"
                title="Re-send invite"
                onClick={() => void handleResend(inv)}
                className="p-1.5 rounded-md text-khiimori-text-muted hover:text-blue-500 hover:bg-blue-500/10 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                type="button"
                title="Cancel invitation"
                onClick={() => setCancelTarget(inv)}
                className="p-1.5 rounded-md text-khiimori-text-muted hover:text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => void handleCancel()}
        title="Cancel invitation"
        message={
          cancelTarget
            ? `Cancel the invitation sent to ${cancelTarget.email}? They will no longer be able to accept.`
            : ''
        }
        confirmText="Cancel invite"
        variant="warning"
      />
    </Card>
  );
};

export default PendingInvitesCard;
